import React, { useState, useEffect } from 'react';
import { useAddin } from '../App';
import * as webhook from '../services/webhook';
import ContactModal from '../components/ContactModal';

/**
 * Matter tab — detailed matter view with hero card, details,
 * contacts, deadlines, and recent activity.
 */
export default function MatterTab() {
    const { linkedMatter, userEmail } = useAddin();
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showContactModal, setShowContactModal] = useState(false);
    const [selectedContactId, setSelectedContactId] = useState(null);

    // Initial matter info from lookup
    const matter = linkedMatter;

    useEffect(() => {
        const matterId = matter?.id || matter?.matter_id;
        if (!matterId) {
            setLoading(false);
            return;
        }

        let isMounted = true;
        setLoading(true);
        webhook.getMatterDetails({ userEmail, matterId })
            .then(res => {
                if (isMounted) setDetails(res.matter);
            })
            .catch(err => {
                console.error("Failed to fetch matter details:", err);
                if (isMounted) setError(err.message);
            })
            .finally(() => {
                if (isMounted) setLoading(false);
            });

        return () => { isMounted = false; };
    }, [matter?.id, matter?.matter_id, userEmail]);

    if (!matter) {
        return (
            <div className="addin-body" style={{ padding: '20px', textAlign: 'center', color: '#8a8a8a' }}>
                <p>No matter linked.</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="addin-body" style={{ padding: '20px', textAlign: 'center', color: '#8a8a8a' }}>
                <p>Loading matter details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="addin-body" style={{ padding: '20px', color: 'var(--orange)' }}>
                <p>Error loading details: {error}</p>
            </div>
        );
    }

    const financials = details?.financials || {};
    const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);

    return (
        <div className="addin-body">
            {/* Matter hero card */}
            <div className="matter-hero">
                <div className="mh-title">{details?.description || matter.description || matter.name || matter.matter_description}</div>
                <div className="mh-meta">
                    <span className="mh-num">{details?.display_number || details?.number || matter.number || matter.matter_number}</span>
                    <div className="mh-divider" />
                    <span className="mh-area">{details?.practice_area || matter.practiceArea || matter.practice_area || 'Uncategorized'}</span>
                    <span className={`badge ${(details?.status || matter.status || 'open').toLowerCase()}`}>
                        {details?.status || matter.status || 'Open'}
                    </span>
                </div>

                {/* Financials - Fixed Fee Focus */}
                <div className="matter-stats" style={{ marginTop: '16px' }}>
                    <div className="mstat">
                        <div className="mv">{formatCurrency(financials.total_activity_billed)}</div>
                        <div className="ml">Fixed Fee Total</div>
                    </div>
                    <div className="mstat">
                        <div className="mv">{formatCurrency(financials.total_expenses)}</div>
                        <div className="ml">Expenses</div>
                    </div>
                    <div className="mstat" style={{ borderLeft: '1px solid var(--border-color)', paddingLeft: '12px' }}>
                        <div className="mv" style={{ color: 'var(--teal)' }}>{formatCurrency(financials.grand_total)}</div>
                        <div className="ml">Total Billed</div>
                    </div>
                </div>
            </div>

            {/* Matter details & Custom Fields */}
            <div className="section-label">Matter Details</div>
            <div className="info-block">
                <div className="info-row">
                    <div className="info-key">Clio Link</div>
                    <div className="info-val link" onClick={() => {
                        const mId = details?.id || matter?.id || matter?.matter_id;
                        if (mId) window.open(`https://app.clio.com/nc/#/matters/${mId}`, '_blank');
                    }}>
                        Open in Clio ↗
                    </div>
                </div>
                {details?.custom_fields?.map((cf, i) => (
                    <div className="info-row" key={cf.id || i}>
                        <div className="info-key">{cf.name || 'Custom Field'}</div>
                        <div className="info-val">{cf.value ? cf.value.toString() : '—'}</div>
                    </div>
                ))}
            </div>

            {/* Contacts & Relationships */}
            <div className="section-label">Contacts</div>
            {details?.client && (
                <div className="contact-card">
                    <div className="contact-avatar">
                        {(details.client.name?.[0] || 'C').toUpperCase()}
                    </div>
                    <div className="contact-info">
                        <div className="contact-name">
                            {details.client.id ? (
                                <span className="link" onClick={() => { setSelectedContactId(details.client.id); setShowContactModal(true); }}>
                                    {details.client.name}
                                </span>
                            ) : (
                                details.client.name
                            )}
                        </div>
                        <div className="contact-role">Client</div>
                        <div className="contact-email">{details.client.email_addresses?.[0]?.address || 'No email'}</div>
                    </div>
                </div>
            )}
            {details?.contacts?.map((contact) => (
                <div className="contact-card" key={contact.id}>
                    <div className="contact-avatar teal-av">
                        {(contact.name?.[0] || 'R').toUpperCase()}
                    </div>
                    <div className="contact-info">
                        <div className="contact-name">
                            {contact.id ? (
                                <span className="link" onClick={() => { setSelectedContactId(contact.id); setShowContactModal(true); }}>
                                    {contact.name}
                                </span>
                            ) : (
                                contact.name
                            )}
                        </div>
                        <div className="contact-role">{contact.relationship || 'Related'}</div>
                        <div className="contact-email">{(typeof contact.email === 'object' ? contact.email?.address : contact.email) || 'No email'}</div>
                    </div>
                </div>
            ))}
            {(!details?.client && (!details?.contacts || details.contacts.length === 0)) && (
                <div style={{ color: '#8a8a8a', fontSize: '12px', padding: '0 4px' }}>No contacts linked.</div>
            )}
            
            <ContactModal
                isOpen={showContactModal}
                onClose={() => setShowContactModal(false)}
                contactId={selectedContactId}
            />
        </div>
    );
}
